/**
 * Intake-related strings.
 */
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Icons from '../ui/IconButton/Icons';

const Configure = <b>Configure</b>
const Lead = <b>Lead</b>;
const Case = <b>Case</b>;
const Contact = <b>Contact</b>;
const Leads = <b>Leads</b>;
const Cases = <b>Cases</b>;
const Contacts = <b>Contacts</b>;
const Intake = <b>Intake</b>;
const configIcon = <FontAwesomeIcon icon={Icons.CONFIG.reactIcon} size={'lg'}/>;
const privateIcon = <FontAwesomeIcon icon={Icons.PRIVATE.reactIcon} color='red'/>;
const redFlagIcon = <FontAwesomeIcon icon={Icons.FLAG.reactIcon} color='maroon'/>;

export default {
    // Used outside the client portal
    d_title: 'Intake',
    h_title: <div>
        <p>This section is used to fill in intake questions (custom fields) that you created in the Intake section in
            Configuration.</p>
        <p>To set up intake questions, click on the {configIcon} {Configure} button on the right side of the title
            bar.</p>
        <p>Encrypted fields will have a {privateIcon} icon next to them to indicate that they are stored
            encrypted in the database.</p>
    </div>,

    d_portal_title: 'Intake - Please Fill Out!',
    h_portal_title: 'Please answer these questions to the best of your ability. If you have any questions, please contact a member of our team and we will help you!',

    d_titleConfig: <span>Intake</span>,
    h_titleConfig: <div>
        <h1>Intake (Custom Fields)</h1>
        <h2>Purpose</h2>
        <p>This section is used to create custom fields that appear in the Intake section
            for {Contacts}, {Leads} and {Cases}.</p>
        <h2>The "Related To" Dropdown</h2>
        <p>At the top of the page you will se a dropdown box with three values: {Contact}, {Lead} and {Case}. Selecting
            one of these values shows the custom fields related to the selection. For example, if you choose {Lead}, a
            list of custom fields shown in the {Intake} section for a {Lead} will be shown.</p>
        <h2>Attributes</h2>
        <p>The list of intake questions shows all of the custom fields and where there are used.</p>
        <ul>
            <li><b>Question</b> is the label for the custom field that will be shown in the Intake section.</li>
            <li><b>Applies To</b> is only used with {Leads} and {Cases}, and shows which case types the custom field
                should be used with. An empty value means that the custom field should be used with all case types.
            </li>
            <li><b>Answer Type</b> refers to the type of response expected for this question, e.g. Date or Text.</li>
            <li><b>Encrypted</b> indicates if answers for this question is encrypted in the database.</li>
            <li><b>Hidden</b> indicates if the question is hidden from the client in the Client Portal.</li>
        </ul>
    </div>,

    // Used in the client Portal
    d_titleFor: (params: {label: string}) => `Intake for ${params.label}`,
    h_titleFor: <div>
        <p>Client Portal</p>
    </div>,

    d_answerType: 'Answer Type',

    d_relatedTo: 'Related To',

    d_nope: 'Can not change the type of a question after it has been populated with responses',

    d_appliesTo: 'Applies To',
    h_appliesTo: '<div><p>If this question only pertains to a particular type of case, enter the case type(s) here.</p><p>If this question applies to all cases, leave this field blank.</p></div>',

    d_expects: 'Expects',
    h_expects: '<div><p>What kind of answer does this question expect?</p><p>Choose one type from the following list:</p><ul><li><b>Text</b> fields are simple, one-line entry fields.</li><li><b>Date</b> fields ask for a date only, using a date selection widget.</li><li><b>DateTime</b> fields ask for a date and a time using a date/time selection widget.</li><li><b>Time</b> fields ask for a time only, no date, using a time selection widget.</li><li><b>Number</b> fields take only numeric values.</li><li><b>Money</b> fields take numeric values in dollars and cents using a money input widget.</li><li><b>Checkbox</b> fields present a simple checkbox, and are good for yes/no fields.</li><li><b>Multiple Choice</b> fields allow entry of multiple text answers.</li><li><b>Notes</b> presents a multiline input field, good for open ended questions.</li><li><b>Contact(s)</b> will present a contact selection dropdown where you can select any contact in the system. <i>Note: this should never be used on a client-facing form! Always select "Hide from Client" when using Contact fields.</i></li><li><b>User(s)</b> will present a user selection dropdown where you can select any user in the system. It is OK to present a User field to on the client portal as long as you are OK with the client knowing all of the users of your system, including your bookkeeper, contract attorneys, etc. If not, then choose the "Hide from Client" option!</li></ul></div>',

    d_allowMultiples: 'Allow Multiples',
    h_allowMultiples: '<div><p>When checked, the user will be able to select more than one response.</p><p>This works best with things like users and contacts.</p></div>',

    d_choices: 'Choices',
    h_choices: 'If you would like to limit responses to a predefined set of choices, enter them here.',

    d_mustBeOnline: 'Must be online to change encryption on an existing field',

    d_question: 'Question',
    h_question: <div>
        <p>The label for the question when presented in the {Intake} section of a {Contact}, {Lead} or {Case}.</p>
    </div>,

    d_showInCase: 'Show In Case?',
    h_showInCase: <div>
        <p>Should this question also show up in {Cases}?</p>
        <p>If checked, this question will show up in {Leads} as well as {Cases}.  The value entered in the {Lead}
        will also show up in the {Case}.</p>
        </div>,

    d_hidden: 'Hidden',

    d_hideFromClient: 'Hide From Client',
    h_hideFromClient: <div>
        <p>Should this question be hidden from clients when they use the client portal?</p>
        <p>This is useful for custom fields that you want you or your staff to update, rather than the client.</p>
        <p>For example, you might want to track the docket number for the case, which the client may not know.</p>
        <p>This is not a good place to track negative information, as that is what {redFlagIcon} <b>Red Flags</b> are
            for.</p>
        <p>For example, if you want to track whether the client is overly emotional, that would best be tracked
            as a <b>Red Flag</b> in order to show the risk related to the client. Putting the field here will not affect
            the risk reported in the {Leads} and {Cases} pages, which means lost information.</p>
    </div>,

    d_sampleAnswers: 'Sample answers: ',

    d_encrypted: <span>Encrypted {privateIcon}</span>,
    h_encrypted: '<div>Should this field be stored encrypted? Note that all data is transmitted using SSL encryption from the web browser to the server, and from the web server to the database server. Selecting the encrypted checkbox adds the additional protection of storing the values encrypted in the database. This is required for all personally identifiable information (PII) specified in the HIPAA training that you take every year.</div>'


};